import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Star, Crown, Target as TargetIcon } from "lucide-react";
import { Link } from "react-router-dom";

const PERF_LABEL = { 1: "Below", 2: "Meets", 3: "Exceeds" };
const POT_LABEL = { 1: "Limited", 2: "Growth", 3: "High" };
const BOX_INFO = {
  "3,1": { label: "Solid Performer", accent: "border-emerald-300 bg-emerald-50/50" },
  "3,2": { label: "High Performer", accent: "border-emerald-300 bg-emerald-50/50" },
  "3,3": { label: "Star", accent: "border-emerald-400 bg-emerald-100/60" },
  "2,1": { label: "Effective", accent: "border-slate-300 bg-slate-50/50" },
  "2,2": { label: "Core Player", accent: "border-slate-300 bg-slate-50/50" },
  "2,3": { label: "High Potential", accent: "border-violet-300 bg-violet-50/50" },
  "1,1": { label: "Risk", accent: "border-rose-300 bg-rose-50/50" },
  "1,2": { label: "Inconsistent", accent: "border-amber-300 bg-amber-50/50" },
  "1,3": { label: "Rough Diamond", accent: "border-amber-300 bg-amber-50/50" },
};

export default function Succession() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/calibration/succession").then((r) => {
      setItems(r.data.items || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const out = {};
    for (let perf = 1; perf <= 3; perf++) {
      for (let pot = 1; pot <= 3; pot++) out[`${perf},${pot}`] = [];
    }
    items.forEach((p) => {
      const key = `${p.nine_box?.performance ?? 2},${p.nine_box?.potential ?? 2}`;
      if (out[key]) out[key].push(p);
    });
    return out;
  }, [items]);

  const hipos = items.filter((i) => i.is_hipo);
  const successors = items.filter((i) => i.successor_for);

  return (
    <div className="max-w-7xl space-y-6" data-testid="succession-page">
      <div>
        <div className="axia-overline">HR · Succession</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">Succession & 9-box</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-xl">Read-only snapshot from the most-recent committed calibration session per employee.</p>
      </div>

      {loading ? <div className="text-sm text-slate-500">Loading…</div> : items.length === 0 ? (
        <div className="axia-card p-12 text-center text-slate-500" data-testid="empty-succession">
          <TargetIcon className="w-7 h-7 mx-auto text-slate-300" />
          <h2 className="font-display text-xl font-semibold text-slate-900 mt-3">No committed calibration yet</h2>
          <p className="text-sm mt-1">Run + commit a <Link to="/calibration" className="underline">calibration session</Link> to populate the 9-box.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Stat icon={<Star className="w-4 h-4 text-violet-600" />} label="HiPo flagged" value={hipos.length} />
            <Stat icon={<Crown className="w-4 h-4 text-amber-600" />} label="Successors mapped" value={successors.length} />
            <Stat icon={<TargetIcon className="w-4 h-4 text-emerald-600" />} label="Total in 9-box" value={items.length} />
          </div>

          <div className="axia-card p-6">
            <h2 className="font-display text-base font-semibold text-slate-900 mb-3">9-box snapshot</h2>
            <div className="grid grid-cols-[auto_1fr] gap-2">
              <div className="flex flex-col-reverse justify-around items-end pr-2 text-[11px] uppercase tracking-wider text-slate-500 py-2">
                <span>Below ↓</span><span>Meets</span><span>Exceeds ↑</span>
              </div>
              <div>
                <div className="grid grid-cols-3 gap-3">
                  {[3, 2, 1].map((perf) => [1, 2, 3].map((pot) => {
                    const k = `${perf},${pot}`;
                    const info = BOX_INFO[k];
                    const cellItems = grouped[k] || [];
                    return (
                      <div key={k} className={`min-h-[140px] rounded-lg border-2 ${info.accent} p-3`} data-testid={`succ-9box-${perf}-${pot}`}>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{info.label}</div>
                        <div className="text-[10px] text-slate-400 mb-2">{PERF_LABEL[perf]} · {POT_LABEL[pot]}</div>
                        <div className="space-y-1">
                          {cellItems.map((p) => (
                            <Link
                              key={p.employee_id}
                              to={`/profile/${p.employee_id}`}
                              className={`block px-2 py-1 rounded-md text-xs truncate ${p.is_hipo ? "bg-violet-50 text-violet-900 border border-violet-200" : "bg-white text-slate-800 border border-slate-200"} hover:border-slate-400 transition`}
                            >
                              {p.is_hipo && <Star className="inline w-3 h-3 text-violet-600 mr-1" />}
                              {p.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    );
                  }))}
                </div>
                <div className="grid grid-cols-3 mt-2 text-[11px] uppercase tracking-wider text-slate-500 text-center">
                  <span>← Limited potential</span><span>Growth</span><span>High potential →</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="axia-card p-5">
              <h3 className="font-display text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><Star className="w-4 h-4 text-violet-600" /> HiPo bench</h3>
              {hipos.length === 0 ? <div className="text-xs text-slate-400">None flagged.</div> : (
                <ul className="space-y-2">
                  {hipos.map((p) => (
                    <li key={p.employee_id} className="text-sm" data-testid={`hipo-${p.employee_id}`}>
                      <Link to={`/profile/${p.employee_id}`} className="font-medium text-slate-900 hover:underline">{p.name}</Link>
                      <span className="text-xs text-slate-500"> · {p.designation} · {p.department}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="axia-card p-5">
              <h3 className="font-display text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><Crown className="w-4 h-4 text-amber-600" /> Successor map</h3>
              {successors.length === 0 ? <div className="text-xs text-slate-400">No successors mapped yet.</div> : (
                <ul className="space-y-2">
                  {successors.map((p) => (
                    <li key={p.employee_id} className="text-sm" data-testid={`succ-${p.employee_id}`}>
                      <Link to={`/profile/${p.employee_id}`} className="font-medium text-slate-900 hover:underline">{p.name}</Link>
                      <span className="text-xs text-slate-500"> → successor for </span>
                      <Link to={`/profile/${p.successor_for}`} className="text-xs underline text-slate-700">view leader</Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="axia-card p-4">
      <div className="flex items-center gap-2">{icon}<div className="axia-overline">{label}</div></div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}
